// src/hooks/useIncidentQueue.js

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { compareBySeverity } from "../constants/severity";
import { isActiveStatus } from "../constants/incidentStatus";
import { summarizeQueue } from "../utils/queueMetrics";
import { saveHistoryForBothUsers } from "../services/historyService";
import {
  finalizeCompletionIfMutual,
  markClientDone,
  markHelperDone,
} from "../services/readinessService";
import { deleteProblem } from "../services/problemService";

/**
 * The incident queue for one participant, plus the actions that change it.
 *
 * Both dashboards previously carried their own copy of this listener and of
 * every handler below — mark solved, request refund, dismiss, cancel, save
 * history — which is how they drifted apart. The two-party confirmation dance
 * in particular is subtle enough that having two implementations of it was a
 * standing invitation for them to disagree about when an incident is closed.
 *
 * @param {object} options
 * @param {object} options.currentUser Firebase auth user.
 * @param {"client"|"expert"} options.viewerRole
 */
export function useIncidentQueue({ currentUser, viewerRole }) {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isExpert = viewerRole === "expert";
  const ownerField = isExpert ? "helperId" : "clientId";

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const incidentQuery = query(
      collection(db, "problems"),
      where(ownerField, "==", currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      incidentQuery,
      (snapshot) => {
        setProblems(
          snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }))
        );
        setLoading(false);

        // Announce transitions the viewer did not trigger themselves.
        snapshot.docChanges().forEach((change) => {
          if (change.type !== "modified") return;
          const data = change.doc.data();
          if (data.status === "completed") {
            toast.success("Incident resolved and closed for both parties.");
          } else if (data.status === "assigned" && !isExpert) {
            toast.info("An expert has picked up your incident.");
          }
        });
      },
      (error) => {
        console.error("Incident queue subscription error:", error);
        setLoading(false);
        toast.error("Could not load your incidents.");
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid, ownerField, isExpert]);

  // Worst first, then oldest. The raw query has no ordering, and recency is the
  // wrong priority for a queue: a P1 checkout outage from an hour ago outranks
  // a P4 question from a minute ago.
  const active = useMemo(
    () =>
      problems.filter((p) => isActiveStatus(p.status)).sort(compareBySeverity),
    [problems]
  );

  const completed = useMemo(
    () =>
      problems
        .filter(
          (p) =>
            (p.status === "completed" || p.status === "unresolved") &&
            !p.hiddenBy?.includes(currentUser?.uid)
        )
        .sort(
          (a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)
        ),
    [problems, currentUser?.uid]
  );

  const summary = useMemo(() => summarizeQueue(problems), [problems]);

  /** Writes the shared history record once an incident is genuinely closed. */
  const archiveIncident = useCallback(
    async (problem, status) => {
      const result = await saveHistoryForBothUsers({
        taskId: problem.id,
        title: problem.title,
        description: problem.description,
        domain: problem.domain || problem.category || "General",
        clientId: problem.clientId,
        helperId: problem.helperId || null,
        clientName:
          problem.clientName || problem.clientEmail || "Customer",
        helperName: problem.helperName || "Specialist",
        rating: problem.rating || null,
        attachment: problem.attachment || null,
        createdAt: problem.createdAt,
        assignedAt: problem.assignedAt,
        ...(status ? { status } : {}),
        incidentReportId: problem.incidentReportId || null,
      });

      if (!result.success) {
        toast.warning(
          `Incident closed, but archiving it had issues: ${result.error}`
        );
      }
      return result;
    },
    []
  );

  const finalizeResolution = useCallback(
    async (problem) => {
      try {
        if (isExpert) {
          await markHelperDone(problem.id, currentUser.uid);
        } else {
          await markClientDone(problem.id, currentUser.uid);
        }

        const outcome = await finalizeCompletionIfMutual(problem.id);

        if (!outcome.success) {
          toast.error(`Could not close the incident: ${outcome.error}`);
          return { success: false };
        }

        if (!outcome.completed) {
          toast.info(
            isExpert
              ? "Recorded. The incident closes once the customer also confirms."
              : "Recorded. The incident closes once your expert also confirms."
          );
          return { success: true, completed: false };
        }

        await archiveIncident(outcome.problem || problem);
        toast.success("Incident resolved and closed for both parties.");
        return { success: true, completed: true };
      } catch (error) {
        console.error("Error finalizing resolution:", error);
        toast.error(`Could not close the incident: ${error.message}`);
        return { success: false };
      }
    },
    [archiveIncident, currentUser?.uid, isExpert]
  );

  /**
   * Records this party's confirmation and closes the incident only once both
   * sides agree. Returns `{ needsReport: true }` when an expert has to file the
   * resolution report first — the caller owns that modal.
   *
   * Declared after finalizeResolution so the memoised callback always closes
   * over the current one rather than a stale render's copy.
   */
  const confirmResolution = useCallback(
    async (problemId) => {
      if (!currentUser?.uid) {
        toast.error("You must be signed in.");
        return { success: false };
      }

      try {
        const snap = await getDoc(doc(db, "problems", problemId));
        if (!snap.exists()) {
          toast.error("Incident not found.");
          return { success: false };
        }

        const problem = { id: snap.id, ...snap.data() };

        if (problem.status === "completed") {
          toast.info("This incident is already closed.");
          return { success: false };
        }

        // The report defines what was fixed and scopes the refund guarantee, so
        // it is a precondition for the expert's confirmation, not a follow-up.
        if (isExpert && !problem.incidentReportId) {
          return { success: false, needsReport: true, problem };
        }

        return await finalizeResolution(problem);
      } catch (error) {
        console.error("Error confirming resolution:", error);
        toast.error(`Could not confirm resolution: ${error.message}`);
        return { success: false };
      }
    },
    [currentUser?.uid, isExpert, finalizeResolution]
  );

  /** Expert-only: close an incident we could not fix, and open the refund. */
  const closeUnresolved = useCallback(
    async (problem) => {
      try {
        await updateDoc(doc(db, "problems", problem.id), {
          status: "unresolved",
          refundEligible: true,
          refundRequestedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        await addDoc(collection(db, "problems", problem.id, "messages"), {
          type: "system",
          text: "This incident was closed as unresolved. Operations has been notified and the customer is eligible for a full refund.",
          createdAt: serverTimestamp(),
          senderId: currentUser.uid,
        });

        await archiveIncident(problem, "unresolved");
        toast.info(
          "Closed as unresolved. Operations and the customer have been notified."
        );
        return { success: true };
      } catch (error) {
        toast.error(`Could not close the incident: ${error.message}`);
        return { success: false };
      }
    },
    [archiveIncident, currentUser?.uid]
  );

  const requestRefund = useCallback(
    async (problemId) => {
      try {
        await updateDoc(doc(db, "problems", problemId), {
          refundEligible: true,
          refundRequestedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await addDoc(collection(db, "problems", problemId, "messages"), {
          type: "system",
          text: "A refund was requested. Operations has been notified and will process it within 24 hours.",
          createdAt: serverTimestamp(),
          senderId: currentUser.uid,
        });
        toast.success("Refund requested. Operations will follow up within 24 hours.");
      } catch (error) {
        toast.error(`Could not request the refund: ${error.message}`);
      }
    },
    [currentUser?.uid]
  );

  const dismissIncident = useCallback(
    async (problemId) => {
      try {
        await updateDoc(doc(db, "problems", problemId), {
          hiddenBy: arrayUnion(currentUser.uid),
        });
      } catch (error) {
        toast.error(`Could not hide the incident: ${error.message}`);
      }
    },
    [currentUser?.uid]
  );

  /** Client-only: withdraw an incident nobody has claimed yet. */
  const cancelIncident = useCallback(async (problemId) => {
    const result = await deleteProblem(problemId);
    if (result.success) {
      toast.success("Incident withdrawn.");
    } else {
      toast.error(`Could not withdraw the incident: ${result.error}`);
    }
    return result;
  }, []);

  return {
    problems,
    active,
    completed,
    summary,
    loading,

    confirmResolution,
    finalizeResolution,
    closeUnresolved,
    requestRefund,
    dismissIncident,
    cancelIncident,
  };
}

export default useIncidentQueue;
